import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Alert from 'flarum/common/components/Alert';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import Stream from 'flarum/common/utils/Stream';
import dynamicallyLoadLib from '../utils/dynamicallyLoadLib';

/**
 * Modal for editing event metadata (start, end, location, event_type).
 * Opened from the discussion page via "Edit Event Details" button.
 */
export default class EditEventModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    const event = this.attrs.event;

    this.event_start   = Stream(event?.event_start() ? event.event_start() : null);
    this.event_end     = Stream(event?.event_end()   ? event.event_end()   : null);
    this.location      = Stream(event?.location()    || '');
    this.event_type    = Stream(event?.event_type()  || '');

    // Duration in minutes, derived from start/end, editable directly
    const existingDuration = event?.duration_minutes() || 60;
	  this.durationHours = Stream(existingDuration ? existingDuration / 60 : 1);
  }

  title() {
    return app.translator.trans('kemze-calendar.forum.modal.title_edit');
  }

  className() {
    return 'EditEventsModal Modal--medium';
  }

  content() {
    const eventTypes = this.getEventTypes();
    const locations  = this.getLocations();

    return (
      <div className="Modal-body">

        <div className="Form-group">
          <label>{app.translator.trans('kemze-calendar.forum.modal.start_label')}</label>
          <input
            className="FormControl"
            data-input
            oncreate={this.initDatePicker.bind(this)}
          />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('kemze-calendar.forum.modal.duration_label')}</label>
          <div className="EventModal-duration">
	    <input
	    type="number"
	    min="0.5"
	    step="0.5"
	    className="FormControl"
	    value={this.durationHours()}
	    oninput={(e) => {
		    this.durationHours(parseFloat(e.target.value) || 1);
		    this.recomputeEnd();
	    }}
	    />
	    <span className="EventModal-durationUnit">
	    {app.translator.trans('kemze-calendar.forum.modal.duration_minutes')}
	    </span>
	    </div>
        </div>

        {locations.length > 0 && (
          <div className="Form-group">
            <label>{app.translator.trans('kemze-calendar.forum.modal.location_label')}</label>
            <Select
              value={this.location()}
              options={locations}
              onchange={(val) => this.location(val)}
            />
          </div>
        )}

        {eventTypes.length > 0 && (
          <div className="Form-group">
            <label>{app.translator.trans('kemze-calendar.forum.modal.event_type_label')}</label>
            <Select
              value={this.event_type()}
              options={eventTypes}
              onchange={(val) => this.event_type(val)}
            />
          </div>
        )}

        <div className="Form-group">
          <Button
            type="submit"
            className="Button Button--primary"
            loading={this.loading}
          >
            {app.translator.trans('kemze-calendar.forum.modal.submit')}
          </Button>
        </div>
      </div>
    );
  }

  getEventTypes() {
    try {
      const raw = app.forum.attribute('kemze-calendar.event_types') || '[]';
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(parsed)) return {};
      const opts = { '': '— ' + app.translator.trans('kemze-calendar.forum.modal.no_type') + ' —' };
      parsed.forEach((t) => { opts[t.name] = t.name; });
      return opts;
    } catch {
      return {};
    }
  }

  getLocations() {
    try {
      const raw = app.forum.attribute('kemze-calendar.locations') || '[]';
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(parsed)) return {};
      const opts = { '': '— ' + app.translator.trans('kemze-calendar.forum.modal.no_location') + ' —' };
      parsed.forEach((l) => { opts[l] = l; });
      return opts;
    } catch {
      return {};
    }
  }

  recomputeEnd() {
    if (this.event_start()) {
      const start = new Date(this.event_start());
	    const end = new Date(start.getTime() + this.durationHours() * 3600000);
      this.event_end(end);
    }
  }

  async initDatePicker(vnode) {
    const userLang = app.translator.getLocale();
    await dynamicallyLoadLib('flatpickr');
    await dynamicallyLoadLib('flatpickrLocale', userLang);

    const locale = flatpickr.l10ns[userLang] || flatpickr.l10ns.default;

    const defaultDate = this.event_start() ? new Date(this.event_start()) : new Date();

    flatpickr(vnode.dom, {
      enableTime: true,
      time_24hr: true,
      dateFormat: 'Y-m-d H:i',
	    locale: { ...locale, firstDayOfWeek: 1 },
      defaultDate,
      inline: true,
      onChange: (dates) => {
        if (dates[0]) {
          this.event_start(dates[0]);
          this.recomputeEnd();
        }
      },
    });

    m.redraw();
  }

  async onsubmit(e) {
    e.preventDefault();

    if (!this.event_start()) {
      app.alerts.show(Alert, { type: 'error' },
        app.translator.trans('kemze-calendar.forum.modal.requirement_message'));
      return;
    }

    this.loading = true;
    m.redraw();

    try {
      const updated = await this.attrs.event.save({
        event_start: this.event_start() instanceof Date
          ? this.event_start().toISOString()
          : this.event_start(),
        event_end: this.event_end() instanceof Date
          ? this.event_end().toISOString()
          : this.event_end(),
        location:   this.location()   || null,
        event_type: this.event_type() || null,
      });

      if (this.attrs.onSave) {
        this.attrs.onSave(updated);
      }

      this.hide();
    } catch (err) {
      this.loading = false;
      m.redraw();
    }
  }
}
